import { CURRENCY_META } from "@/lib/format";

// Charging happens in INR (₹799 / ₹1,999 / ₹5,999). Other currencies are display-only conversions.
export const INR_PRICES = { monthly: 799, quarterly: 1999, yearly: 5999 };

export const DISPLAY_RATES = { INR: 1, USD: 0.012, EUR: 0.011, GBP: 0.0095, AED: 0.044, SGD: 0.016, AUD: 0.018 };

export const PRICE_BOOK = Object.fromEntries(
  Object.entries(DISPLAY_RATES).map(([code, rate]) => [
    code,
    {
      monthly: Math.max(1, Math.round(INR_PRICES.monthly * rate)),
      quarterly: Math.max(1, Math.round(INR_PRICES.quarterly * rate)),
      yearly: Math.max(1, Math.round(INR_PRICES.yearly * rate)),
    },
  ])
);

export const CYCLES = [
  {
    key: "monthly",
    label: "Monthly",
    months: 1,
    proposals: 50,
    bonus: 0,
    save: 0,
    note: "50 proposals every month",
  },
  {
    key: "quarterly",
    label: "Quarterly",
    months: 3,
    proposals: 165,
    bonus: 15,
    save: 17,
    note: "150 proposals + 15 bonus",
  },
  {
    key: "yearly",
    label: "Yearly",
    months: 12,
    proposals: 700,
    bonus: 100,
    save: 37,
    note: "600 proposals + 100 bonus",
    popular: true,
  },
];

const ZONE_MAP = [
  [/^Asia\/(Kolkata|Calcutta)/, "INR"],
  [/^Asia\/Dubai/, "AED"],
  [/^Asia\/Singapore/, "SGD"],
  [/^Australia\//, "AUD"],
  [/^Europe\/London/, "GBP"],
  [/^Europe\//, "EUR"],
  [/^(America|US)\//, "USD"],
  [/^Pacific\/Auckland/, "AUD"],
];

const LOCALE_MAP = { IN: "INR", AE: "AED", SG: "SGD", AU: "AUD", NZ: "AUD", GB: "GBP", US: "USD", CA: "USD" };
const EURO_REGIONS = ["DE", "FR", "ES", "IT", "NL", "IE", "PT", "BE", "AT", "FI", "GR"];

export function detectCurrency() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const hit = ZONE_MAP.find(([re]) => re.test(zone));
    if (hit) return hit[1];
    const region = (navigator.language || "").split("-")[1]?.toUpperCase();
    if (region && LOCALE_MAP[region]) return LOCALE_MAP[region];
    if (region && EURO_REGIONS.includes(region)) return "EUR";
  } catch (e) {
    console.debug("currency detection fell back to USD", e);
  }
  return "USD";
}

export function priceLabel(amount, currency) {
  const symbol = (CURRENCY_META[currency] || CURRENCY_META.USD).symbol;
  const indian = !!(CURRENCY_META[currency] || {}).indian;
  const rounded = Math.round(amount);
  let digits = String(rounded);
  if (indian && digits.length > 3) {
    const tail = digits.slice(-3);
    let head = digits.slice(0, -3);
    const parts = [];
    while (head.length > 2) {
      parts.unshift(head.slice(-2));
      head = head.slice(0, -2);
    }
    if (head) parts.unshift(head);
    digits = [...parts, tail].join(",");
  } else {
    digits = rounded.toLocaleString("en-US");
  }
  return `${symbol}${digits}`;
}

export function cycleMath(cycleKey, currency) {
  const cycle = CYCLES.find((c) => c.key === cycleKey);
  const book = PRICE_BOOK[currency] || PRICE_BOOK.USD;
  const total = book[cycleKey];
  const perMonth = total / cycle.months;
  const listPrice = book.monthly * cycle.months;
  return {
    cycle,
    total,
    totalLabel: priceLabel(total, currency),
    perMonthLabel: priceLabel(perMonth, currency),
    listLabel: priceLabel(listPrice, currency),
    savedLabel: priceLabel(listPrice - total, currency),
    hasSaving: listPrice > total,
    inrLabel: priceLabel(INR_PRICES[cycleKey], "INR"),
  };
}
